import { useId, type ReactNode } from 'react';

import { cn } from '@mds/shared/lib/utils';

/*
 * Isometric projection. The scene is modelled in world units — `u` runs
 * towards us on the right, `v` towards us on the left, `h` is height — and
 * every point goes through `xy`. He faces `-u`, up and to the left on screen,
 * which is towards the package cards.
 */
const C = Math.cos(Math.PI / 6);
const S = 0.5;
const K = 1.17;
const OX = 208;
const OY = 76;
/** Screen-space sizes (limb widths, the upper body) were tuned at K = 0.88. */
const Z = K / 0.88;

type P3 = [u: number, v: number, h: number];

const xy = (u: number, v: number, h = 0): [number, number] => [
  OX + (u - v) * C * K,
  OY + ((u + v) * S - h) * K,
];
const pt = (u: number, v: number, h = 0) =>
  xy(u, v, h)
    .map((n) => n.toFixed(1))
    .join(',');
const pts = (...ps: P3[]) => ps.map((p) => pt(...p)).join(' ');
const line = (...ps: P3[]) => `M${ps.map((p) => pt(...p)).join(' L')}`;

/**
 * Transforms that lay flat 2D drawing onto a plane of the scene, origin at
 * the world point given, so a drawer front or a screen can be drawn square
 * and land at the right angle.
 *
 * `front`: a face looking towards `+u` (the cupboard front, the laptop
 * screen). Local x runs along `-v`, local y down.
 * `top`: a horizontal face. Local x along `+u`, local y along `+v`.
 */
const front = (u: number, v: number, h: number) => {
  const [x, y] = xy(u, v, h);
  return `matrix(${C * K} ${-S * K} 0 ${K} ${x} ${y})`;
};
const top = (u: number, v: number, h: number) => {
  const [x, y] = xy(u, v, h);
  return `matrix(${C * K} ${S * K} ${-C * K} ${S * K} ${x} ${y})`;
};

/*
 * The monitor, turned in towards him and a little further to the right:
 * its screen faces `MON_N`, 60° off the desk edge rather than a square 45°.
 * A 42-wide screen centred on `MON_C`, front edge from A to B, 3 deep, on a
 * square foot in front of it.
 */
const MON_ANGLE = Math.PI / 6;
const MON_N: [number, number] = [Math.cos(MON_ANGLE), Math.sin(MON_ANGLE)];
const MON_T: [number, number] = [Math.sin(MON_ANGLE), -Math.cos(MON_ANGLE)];
const MON_C: [number, number] = [25, 77];
const along = ([u, v]: [number, number], t: number, n: number): [number, number] => [
  u + t * MON_T[0] + n * MON_N[0],
  v + t * MON_T[1] + n * MON_N[1],
];
const MON_A = along(MON_C, -21, 0);
const MON_B = along(MON_C, 21, 0);
const MON_A_BACK = along(MON_A, 0, -3);
const MON_B_BACK = along(MON_B, 0, -3);
const MON_NECK = along(MON_C, 0, -4.5);
const FOOT_C = along(MON_C, 0, 4.5);
const MON_FOOT = [along(FOOT_C, -8, -7), along(FOOT_C, 8, -7), along(FOOT_C, 8, 7), along(FOOT_C, -8, 7)];

/** Lays flat drawing onto the monitor's screen: local x along `MON_T`, y down. */
const turned = (u: number, v: number, h: number) => {
  const [x, y] = xy(u, v, h);
  const dx = (MON_T[0] - MON_T[1]) * C * K;
  const dy = (MON_T[0] + MON_T[1]) * S * K;
  return `matrix(${dx} ${dy} 0 ${K} ${x} ${y})`;
};

/** A box with its three visible faces: the top, the `+u` face, the `+v` face. */
function Box({
  u: [u0, u1],
  v: [v0, v1],
  h: [h0, h1],
  fill,
  frontFill = fill,
  sideFill = fill,
  strokeWidth = 1.8,
}: {
  u: [number, number];
  v: [number, number];
  h: [number, number];
  fill: string;
  frontFill?: string;
  sideFill?: string;
  strokeWidth?: number;
}) {
  return (
    <g strokeWidth={strokeWidth} className="stroke-ink">
      <polygon points={pts([u0, v0, h1], [u1, v0, h1], [u1, v1, h1], [u0, v1, h1])} className={fill} />
      <polygon points={pts([u1, v0, h1], [u1, v1, h1], [u1, v1, h0], [u1, v0, h0])} className={frontFill} />
      <polygon points={pts([u0, v1, h1], [u1, v1, h1], [u1, v1, h0], [u0, v1, h0])} className={sideFill} />
    </g>
  );
}

/** Faint wood grain, drawn in a face's local coordinates. */
function Grain({ x, y, width, rows, gap }: { x: number; y: number; width: number; rows: number; gap: number }) {
  const step = width / 4;
  return (
    <path
      d={Array.from(
        { length: rows },
        (_, i) => `M${x} ${y + i * gap} q${step / 2} -0.8 ${step} 0 t${step} 0 t${step} 0 t${step} 0`,
      ).join(' ')}
      strokeWidth="0.5"
      className="stroke-ink/15"
    />
  );
}

/**
 * Staggers a looping animation by `n` steps, so the scene's loops never line
 * up and pulse together.
 */
const stagger = (n: number) => ({ animationDelay: `calc(var(--duration-stagger) * ${n})` });

/** A four-pointed sparkle, centred on `x, y`, twinkling `delay` steps late. */
function Sparkle({ x, y, r, delay, className }: { x: number; y: number; r: number; delay: number; className: string }) {
  return (
    <path
      d={`M${x} ${y - r} Q${x} ${y} ${x + r} ${y} Q${x} ${y} ${x} ${y + r} Q${x} ${y} ${x - r} ${y} Q${x} ${y} ${x} ${y - r} Z`}
      style={stagger(delay)}
      className={cn('origin-center transform-fill motion-safe:animate-scene-twinkle', className)}
    />
  );
}

/** A group that bobs gently up and down, `delay` steps out of phase. */
function Float({ delay, children }: { delay: number; children: ReactNode }) {
  return (
    <g style={stagger(delay)} className="motion-safe:animate-scene-float">
      {children}
    </g>
  );
}

/** Thick round-ended limb: an ink outline with a colour inside. */
function Limb({ d, width, className }: { d: string; width: number; className: string }) {
  return (
    <>
      <path d={d} strokeWidth={width + 3} className="stroke-ink" />
      <path d={d} strokeWidth={width} className={className} />
    </>
  );
}

/**
 * A person at a desk with headphones on, editing a video, drawn isometric:
 * the whole scene is turned 45° with him, so he faces up and to the left,
 * towards the package cards it stands beside.
 *
 * Line art in ink with flat fills in the brand roles (mint, dark green, sky,
 * sun, charcoal). Every colour is a token utility rather than an attribute,
 * so the drawing follows the theme files like the rest of the UI. Purely
 * decorative, so it is hidden from assistive tech.
 *
 * The SVG is absolutely positioned inside its box: it fills whatever space it
 * is given and adds no height of its own, so beside a row of cards it takes
 * the row's height instead of setting it.
 */
export default function DeskWorkerIllustration({ className }: { className?: string }) {
  // `useId` output carries characters a `url(#…)` reference can trip on.
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, '');
  const dots = `dots-${uid}`;
  const preview = `preview-${uid}`;

  // Where his hips sit on the seat, and the scale his upper body is drawn at.
  const [hx, hy] = xy(92, 102, 47);
  const body = 0.54 * Z;
  const shoulder: [number, number] = [hx + (236 - 255) * body, hy + (110 - 174) * body];
  const hand = xy(41, 117, 65);
  const elbow: [number, number] = [
    (shoulder[0] + hand[0]) / 2 + 2 * Z,
    (shoulder[1] + hand[1]) / 2 + 7 * Z,
  ];

  const mug = xy(88, 64, 62);
  const shade = xy(25, 160, 88);
  const [bx, by] = xy(19, 170, 62);
  const [jx, jy] = xy(17, 170, 82);

  return (
    <div className={cn('relative', className)} aria-hidden="true">
      <svg
        viewBox="0 0 400 260"
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 size-full"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <defs>
          <pattern id={dots} width="5" height="5" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="0.8" className="fill-brand-edge/40" />
          </pattern>
          {/* The editor's preview frame, in the editor's own coordinates */}
          <clipPath id={preview}>
            <rect x="159" y="101" width="29" height="18" rx="0.5" />
          </clipPath>
        </defs>

        {/* ===== Accents: halftone, sparkles, rings, plus marks ===== */}
        {/* Everything here moves: the halftone and the small marks bob, the
            sparkles twinkle, each on its own offset */}
        <Float delay={0}>
          <circle cx="70" cy="70" r="46" fill={`url(#${dots})`} />
        </Float>
        <Float delay={20}>
          <circle cx="352" cy="214" r="34" fill={`url(#${dots})`} />
        </Float>
        <Float delay={36}>
          <circle cx="348" cy="64" r="38" fill={`url(#${dots})`} />
        </Float>
        <Sparkle x={44} y={34} r={7} delay={0} className="fill-highlight stroke-ink" />
        <Sparkle x={302} y={44} r={6} delay={9} className="fill-brand" />
        <Sparkle x={392} y={140} r={4} delay={17} className="fill-brand-edge" />
        <Sparkle x={30} y={190} r={5} delay={25} className="fill-info-solid" />
        <Sparkle x={262} y={18} r={3.5} delay={31} className="fill-highlight" />
        <Float delay={8}>
          <circle cx="24" cy="150" r="3.5" strokeWidth="1.5" className="stroke-brand-edge" />
        </Float>
        <Float delay={28}>
          <circle cx="300" cy="240" r="3" strokeWidth="1.5" className="stroke-info" />
        </Float>
        <Float delay={14}>
          <path d="M386 112 h8 M390 108 v8" strokeWidth="1.5" className="stroke-brand-edge" />
        </Float>
        <Float delay={42}>
          <path d="M64 236 h7 M67.5 232.5 v7" strokeWidth="1.5" className="stroke-brand-edge" />
        </Float>
        <Float delay={22}>
          <path d="M18 112 q6 -5 12 0 t12 0" strokeWidth="1.5" className="stroke-brand/70" />
        </Float>

        {/* Ground shadows */}
        <polygon
          points={pts([12, 30, 0], [132, 30, 0], [132, 74, 0], [64, 74, 0], [64, 178, 0], [12, 178, 0])}
          className="fill-ink/5"
        />

        {/* Rug the length of the desk, with a fringe at both ends */}
        <polygon points={pts([64, 28, 0], [124, 28, 0], [124, 176, 0], [64, 176, 0])} className="fill-brand/30" />
        <polygon points={pts([70, 34, 0], [118, 34, 0], [118, 170, 0], [70, 170, 0])} strokeWidth="1" strokeDasharray="3 3" className="stroke-brand-edge/50" />
        {[66, 74, 82, 90, 98, 106, 114, 122].map((u) => (
          <path key={u} d={`${line([u, 176, 0], [u, 180, 0])} ${line([u, 28, 0], [u, 24, 0])}`} strokeWidth="1" className="stroke-brand-edge/50" />
        ))}

        {/* ===== Desk and cupboard ===== */}
        <Box u={[14, 18]} v={[32, 36]} h={[0, 57]} fill="fill-panel" strokeWidth={1.2} />
        <Box u={[14, 18]} v={[169, 173]} h={[0, 57]} fill="fill-panel" strokeWidth={1.2} />

        {/* Cupboard at the left end: three drawers, a lock and key on the
            top one, casters underneath */}
        <Box u={[18, 56]} v={[134, 168]} h={[3, 50]} fill="fill-surface" />
        <polygon points={pts([18, 168, 50], [56, 168, 50], [56, 168, 3], [18, 168, 3])} className="fill-ink/10" />
        <g transform={front(56, 168, 50)}>
          {[1.5, 16.5, 31.5].map((y) => (
            <g key={y}>
              <rect x="1.5" y={y} width="31" height="14" rx="0.8" strokeWidth="1" className="fill-surface stroke-ink" />
              <Grain x={3.5} y={y + 3.5} width={27} rows={3} gap={3.3} />
            </g>
          ))}
          <circle cx="17" cy="4.6" r="1.2" strokeWidth="0.6" className="fill-hairline stroke-ink" />
          <path d="M17 5.8 V10 M17 8.8 H18.3" strokeWidth="0.9" className="stroke-ink-muted" />
        </g>
        {(
          [
            [55, 165],
            [55, 137],
          ] as const
        ).map(([u, v]) => {
          const [cx, cy] = xy(u, v, 1.5);
          return <circle key={v} cx={cx} cy={cy} r={1.8 * Z} className="fill-panel" />;
        })}

        <Box u={[56, 60]} v={[169, 173]} h={[0, 57]} fill="fill-panel" strokeWidth={1.2} />
        <Box u={[56, 60]} v={[68, 72]} h={[0, 57]} fill="fill-panel" strokeWidth={1.2} />
        <Box u={[126, 130]} v={[32, 36]} h={[0, 57]} fill="fill-panel" strokeWidth={1.2} />
        <Box u={[126, 130]} v={[68, 72]} h={[0, 57]} fill="fill-panel" strokeWidth={1.2} />

        {/* L-shaped top: the long run he faces, and a return coming towards
            us off its right-hand end */}
        <g strokeWidth="1.8" className="stroke-ink">
          <polygon
            points={pts([12, 28, 62], [130, 28, 62], [130, 72, 62], [62, 72, 62], [62, 176, 62], [12, 176, 62])}
            className="fill-surface"
          />
          <polygon points={pts([130, 28, 62], [130, 72, 62], [130, 72, 57], [130, 28, 57])} className="fill-surface" />
          <polygon points={pts([62, 72, 62], [130, 72, 62], [130, 72, 57], [62, 72, 57])} className="fill-surface" />
          <polygon points={pts([62, 72, 62], [62, 176, 62], [62, 176, 57], [62, 72, 57])} className="fill-surface" />
          <polygon points={pts([12, 176, 62], [62, 176, 62], [62, 176, 57], [12, 176, 57])} className="fill-surface" />
        </g>
        <g transform={top(12, 28, 62)}>
          <path d="M10 6 V142 M22 4 V146 M34 8 V140 M44 5 V144" strokeWidth="0.5" className="stroke-ink/12" />
        </g>
        <g transform={top(62, 28, 62)}>
          <path d="M6 10 H64 M4 22 H66 M8 34 H62" strokeWidth="0.5" className="stroke-ink/12" />
        </g>

        {/* Lamp and its pool of light */}
        <polygon
          points={pts([24, 140, 62], [54, 140, 62], [54, 162, 62], [24, 162, 62])}
          className="fill-highlight/40 motion-safe:animate-scene-glow"
        />
        <g transform={top(30, 143, 62)}>
          <g transform="rotate(-6 9 8)">
            <rect width="18" height="15" rx="0.4" strokeWidth="0.8" className="fill-surface stroke-ink" />
            <path
              d="M3.5 2 q0.6 1.5 0 3 t0 3 t0 3 M6.5 2 q0.6 1.5 0 3 t0 3 t0 3 t0 2 M9.5 2 q0.6 1.5 0 3 t0 3 M12.5 2 q0.6 1.5 0 3 t0 3 t0 3 M15 2 q0.6 1.5 0 3"
              strokeWidth="0.6"
              className="stroke-ink/45"
            />
          </g>
        </g>
        <ellipse cx={bx} cy={by} rx={6 * Z} ry={2.5 * Z} className="fill-panel" />
        <path d={`M${bx} ${by} L${jx} ${jy} L${shade[0]} ${shade[1]}`} strokeWidth={2 * Z} className="stroke-ink" />
        <circle cx={jx} cy={jy} r={1.8 * Z} className="fill-ink" />
        <g transform={`translate(${shade[0]} ${shade[1]}) scale(${-Z} ${Z})`}>
          <path d="M-7 -2 L6 1 L3 9 L-10 4 Z" strokeWidth="1.5" className="fill-highlight stroke-ink" />
        </g>

        {/* The setup from the MDS desks: the laptop on his left, an external
            monitor on his right, raised on a neck over a square foot */}

        {/* Monitor, angled in towards him: foot, neck behind the
            screen, then the screen itself, showing a big preview, colour
            wheels and playback controls */}
        <polygon points={pts(...MON_FOOT.map(([u, v]) => [u, v, 63.5] as P3))} strokeWidth="1.2" className="fill-panel stroke-ink" />
        <polygon
          points={pts([...MON_FOOT[3], 63.5], [...MON_FOOT[2], 63.5], [...MON_FOOT[2], 62], [...MON_FOOT[3], 62])}
          strokeWidth="1.2"
          className="fill-panel stroke-ink"
        />
        <polygon
          points={pts([...MON_FOOT[0], 63.5], [...MON_FOOT[3], 63.5], [...MON_FOOT[3], 62], [...MON_FOOT[0], 62])}
          strokeWidth="1.2"
          className="fill-panel stroke-ink"
        />
        <Box
          u={[MON_NECK[0] - 1.5, MON_NECK[0] + 1.5]}
          v={[MON_NECK[1] - 1.5, MON_NECK[1] + 1.5]}
          h={[63.5, 80]}
          fill="fill-panel"
          strokeWidth={1.2}
        />
        {/* The screen's left end, turned towards us now that it is angled */}
        <polygon
          points={pts([...MON_A_BACK, 98], [...MON_A, 98], [...MON_A, 72], [...MON_A_BACK, 72])}
          strokeWidth="1.6"
          className="fill-panel stroke-ink"
        />
        <polygon
          points={pts([...MON_A_BACK, 98], [...MON_B_BACK, 98], [...MON_B, 98], [...MON_A, 98])}
          strokeWidth="1.6"
          className="fill-panel stroke-ink"
        />
        <polygon
          points={pts([...MON_A, 98], [...MON_B, 98], [...MON_B, 72], [...MON_A, 72])}
          strokeWidth="1.6"
          className="fill-panel stroke-ink"
        />
        <g transform={turned(...MON_A, 98)}>
          <g transform="scale(0.84 0.839)">
          <rect x="1.5" y="1.5" width="47" height="28" rx="0.5" strokeWidth="0.5" className="fill-panel stroke-surface/20" />
          {/* The video editor: media bin, preview, inspector, and the
              timeline with its playhead sweeping across */}
          <g transform="translate(2.5 2.5) scale(0.742 0.6) translate(-142 -98)">
              {/* Media bin */}
            <rect x="145" y="101" width="11" height="5" rx="0.5" className="fill-surface/30" />
            <rect x="145" y="108" width="11" height="5" rx="0.5" className="fill-brand/60" />
            <rect x="145" y="115" width="11" height="4" rx="0.5" className="fill-surface/30" />
            {/* Preview: the clip playing. Its scenery is drawn twice side by
                side and pans one width per lap of the playhead, in step with
                it, clipped to the preview's frame. */}
            <rect x="159" y="101" width="29" height="18" rx="0.5" className="fill-info-solid" />
            <g clipPath={`url(#${preview})`}>
              <g className="motion-safe:animate-scene-pan">
                {[0, 29].map((dx) => (
                  <g key={dx} transform={`translate(${dx} 0)`}>
                    <ellipse cx="165" cy="104.5" rx="3.5" ry="1.3" className="fill-surface/80" />
                    <ellipse cx="180" cy="103.5" rx="2.5" ry="1" className="fill-surface/70" />
                    <path d="M159 119 L167 110 L172 115 L177 108 L188 119 Z" className="fill-brand-strong" />
                    <path d="M159 119 Q166 113 173 117 Q180 112 188 119 Z" className="fill-brand" />
                  </g>
                ))}
              </g>
            </g>
            {/* Inspector */}
            <path d="M191 102 H201 M191 105.5 H199 M191 109 H201 M191 112.5 H197" strokeWidth="1" className="stroke-surface/35" />
            {/* Timeline: two video tracks, an audio waveform, the playhead */}
            <rect x="145" y="124" width="18" height="4" rx="0.8" className="fill-brand" />
            <rect x="164.5" y="124" width="14" height="4" rx="0.8" className="fill-brand" />
            <rect x="180" y="124" width="21" height="4" rx="0.8" className="fill-brand-edge" />
            <rect x="149" y="130" width="22" height="4" rx="0.8" className="fill-info-solid" />
            <rect x="172.5" y="130" width="20" height="4" rx="0.8" className="fill-info-solid" />
            <path
              d="M145 137.5 l2 -1.5 l2 3 l2 -2.5 l2 2 l2 -3 l2 3.5 l2 -2 l2 1.5 l2 -2.5 l2 3 l2 -2 l2 1 l2 -2.5 l2 3 l2 -1.5 l2 2 l2 -3 l2 2.5 l2 -1.5 l2 2 l2 -2 l2 1.5 l2 -1 l2 1"
              strokeWidth="1"
              className="stroke-highlight"
            />
            <path d="M174 121 V140" strokeWidth="1.2" className="stroke-highlight motion-safe:animate-scene-playhead" />
          </g>
          {/* A sticky note on the corner of the bezel */}
          <rect x="41" y="-2" width="7" height="7" transform="rotate(8 44.5 1.5)" strokeWidth="0.5" className="fill-highlight stroke-ink" />
          <path d="M42.5 1 H46 M42.5 2.8 H45" strokeWidth="0.4" className="stroke-ink/50" />
          </g>
        </g>
        {/* Power light */}
        {(() => {
          const [lx, ly] = xy(...along(MON_B, -2, 0.1), 73.2);
          return <circle cx={lx} cy={ly} r="0.9" className="fill-brand" />;
        })()}

        {/* Laptop, dark like the one on the desks: keyboard, trackpad, and
            the lid open on the clip being edited, turned to him */}
        <Box u={[31, 50]} v={[106, 138]} h={[62, 64]} fill="fill-panel" strokeWidth={1.4} />
        <g transform={top(33, 109, 64)}>
          <rect width="11" height="26" rx="0.8" className="fill-surface/10" />
          <path d="M2 1.5 V24.5 M4.5 1.5 V24.5 M7 1.5 V24.5 M9.5 1.5 V24.5" strokeWidth="0.8" strokeDasharray="1.4 0.8" className="stroke-surface/30" />
        </g>
        <g transform={top(45, 117, 64)}>
          <rect width="4" height="10" rx="0.6" className="fill-surface/15" />
        </g>
        <g transform={front(31, 138, 86)}>
          <g transform="scale(0.889 0.917)">
            <rect width="36" height="24" rx="1" strokeWidth="1.3" className="fill-panel stroke-ink" />
            <rect x="2" y="2" width="32" height="20" rx="0.5" strokeWidth="0.5" className="fill-panel stroke-surface/25" />
            {/* The clip itself, full size, with colour wheels and controls */}
            <g transform="translate(2 2) scale(0.696 0.8) translate(-2.5 -2.5)">
              {/* Preview: sky, clouds, hills */}
              <rect x="2.5" y="2.5" width="33" height="20" rx="0.4" className="fill-info-solid" />
              <ellipse cx="11" cy="7" rx="4" ry="1.6" className="fill-surface/80" />
              <ellipse cx="25" cy="5.5" rx="3" ry="1.2" className="fill-surface/70" />
              <path d="M2.5 22.5 L2.5 16 Q10 10 18 15 Q25 9 35.5 14 L35.5 22.5 Z" className="fill-brand-strong" />
              <path d="M2.5 22.5 L2.5 19 Q12 15 22 19 Q29 17 35.5 19.5 L35.5 22.5 Z" className="fill-brand" />
              {/* Colour wheels */}
              {[
                [6.5, 'fill-brand'],
                [13, 'fill-highlight'],
                [19.5, 'fill-info-solid'],
              ].map(([cy, fill]) => (
                <g key={cy}>
                  <circle cx="42" cy={cy} r="2.6" strokeWidth="0.5" className="stroke-surface/50" />
                  <circle cx="42.6" cy={Number(cy) - 0.5} r="0.8" className={String(fill)} />
                </g>
              ))}
              {/* Playback controls */}
              <path d="M3.5 24.5 L6 26 L3.5 27.5 Z" className="fill-surface" />
              <path d="M8.5 26 H45" strokeWidth="0.6" className="stroke-surface/40" />
              <path d="M8.5 26 H22" strokeWidth="0.8" className="stroke-brand" />
              <circle cx="22" cy="26" r="1" className="fill-brand" />
            </g>
          </g>
        </g>

        {/* Notebook and pencil on the return, next to the mug */}
        <Box u={[80, 96]} v={[42, 56]} h={[62, 63.5]} fill="fill-brand-strong" strokeWidth={1.2} />
        <path d={line([82, 44, 63.6], [82, 54, 63.6])} strokeWidth="1" className="stroke-on-brand-strong/50" />
        <path d={line([84, 46, 63.6], [94, 52, 63.6])} strokeWidth={1.8 * Z} className="stroke-highlight" />
        <path d={line([84, 46, 63.6], [84.8, 46.5, 63.6])} strokeWidth={1.8 * Z} className="stroke-ink" />

        {/* Mug */}
        <g transform={`translate(${mug[0]} ${mug[1]}) scale(${Z})`}>
          <path d="M-2 -12 q-2.5 -4 0 -8" strokeWidth="1.2" className="stroke-ink-muted motion-safe:animate-scene-steam" />
          <path
            d="M2 -12 q-2.5 -4 0 -8"
            strokeWidth="1.2"
            style={stagger(20)}
            className="stroke-ink-muted motion-safe:animate-scene-steam"
          />
          <path d="M-4 -9 V0 A4 1.6 0 0 0 4 0 V-9" strokeWidth="1.5" className="fill-info-solid stroke-ink" />
          <ellipse cy="-9" rx="4" ry="1.6" strokeWidth="1.5" className="fill-info stroke-ink" />
          <path d="M4 -7 q3.5 0 3.5 3 q0 3 -3.5 3" strokeWidth="1.5" className="stroke-ink" />
        </g>

        {/* ===== Chair: shadow, base, column, seat ===== */}
        {(() => {
          const [sx, sy] = xy(94, 102, 0);
          return <ellipse cx={sx} cy={sy} rx={20 * Z} ry={10 * Z} className="fill-ink/10" />;
        })()}
        {[0, 72, 144, 216, 288].map((deg) => {
          const a = (deg * Math.PI) / 180 + 0.3;
          const end: P3 = [94 + 17 * Math.cos(a), 102 + 17 * Math.sin(a), 3];
          const [wx, wy] = xy(end[0], end[1], 1.5);
          return (
            <g key={deg}>
              <path d={line([94, 102, 7], end)} strokeWidth={3.5 * Z} className="stroke-panel" />
              <circle cx={wx} cy={wy} r={2.2 * Z} strokeWidth="1" className="fill-panel stroke-ink" />
            </g>
          );
        })}
        <path d={line([94, 102, 7], [94, 102, 40])} strokeWidth={4 * Z} className="stroke-panel" />
        <Box u={[78, 106]} v={[88, 116]} h={[40, 46]} fill="fill-panel" />
        <path d={line([80, 114, 46], [104, 114, 46])} strokeWidth="1" className="stroke-surface/25" />

        {/* ===== Him ===== */}
        {/* Legs: thighs along the seat, knees bent over its front, shins to
            the floor, shoes pointing under the desk. The far leg in shade. */}
        <Limb d={line([90, 95, 50], [58, 95, 51], [56, 95, 7])} width={8 * Z} className="stroke-info" />
        <path d={line([90, 95, 50], [58, 95, 51], [56, 95, 7])} strokeWidth={8 * Z} className="stroke-ink/20" />
        <Limb d={line([57, 95, 4], [47, 95, 4])} width={6 * Z} className="stroke-surface" />
        <Limb d={line([90, 109, 50], [58, 109, 51], [56, 109, 7])} width={8 * Z} className="stroke-info" />
        <Limb d={line([57, 109, 4], [47, 109, 4])} width={6 * Z} className="stroke-surface" />

        {/* Upper body, drawn upright and scaled onto the hips */}
        <g transform={`translate(${hx} ${hy}) scale(${body}) translate(-255 -174)`}>
          <ellipse cx="271" cy="63" rx="3.5" ry="7" strokeWidth="2" className="fill-brand-strong stroke-ink" />
          <path d="M246 90 L262 90 L263 103 L245 103 Z" strokeWidth="2" className="fill-surface stroke-ink" />
          <path
            d="M232 174 C230 146 226 118 236 106 C244 100 264 100 274 106 C284 118 280 146 278 174 Z"
            strokeWidth="2"
            className="fill-brand stroke-ink"
          />
          <path d="M274 106 C284 118 280 146 278 174 L268 174 C272 146 272 120 266 108 Z" className="fill-brand-edge/35" />
          <path d="M254 112 C255 128 254 146 255 170" strokeWidth="1" className="stroke-brand-edge/60" />
          <path
            d="M238 104 Q254 113 270 104 Q262 98 254 98 Q246 98 238 104 Z"
            strokeWidth="2"
            className="fill-brand-strong stroke-ink"
          />
          {/* Head: a sliver of cheek and nose on the left, the rest is hair.
              It nods to whatever is in his headphones, pivoting at the chin. */}
          <g className="origin-bottom transform-fill motion-safe:animate-scene-nod">
          <path d="M234 68 Q227 75 233 81" strokeWidth="2" className="fill-surface stroke-ink" />
          <circle cx="252" cy="70" r="21" strokeWidth="2" className="fill-surface stroke-ink" />
          <path
            d="M232 64 C236 50 252 46 264 50 C276 56 276 76 268 88 C264 92 258 92 252 90 C248 84 246 78 244 72 C240 68 236 66 232 64 Z"
            strokeWidth="2"
            className="fill-panel stroke-ink"
          />
          <path
            d="M248 54 C258 56 266 64 268 76 M242 58 C252 62 258 72 260 84 M256 51 C264 54 270 60 272 68"
            strokeWidth="1"
            className="stroke-surface/30"
          />
          {/* Headphones in the dark brand green */}
          <path d="M241 68 C238 48 262 40 272 57" strokeWidth="5" className="stroke-ink" />
          <path d="M241 68 C238 48 262 40 272 57" strokeWidth="2.5" className="stroke-brand-strong" />
          <ellipse cx="241" cy="77" rx="7.5" ry="10.5" strokeWidth="2" className="fill-brand-strong stroke-ink" />
          <path d="M236 71 Q237 67 241 67" strokeWidth="1.2" className="stroke-surface/50" />
          </g>
        </g>

        {/* His near arm, reaching for the keyboard, his hand tapping at it */}
        <g className="motion-safe:animate-scene-type">
        <Limb
          d={`M${shoulder[0]} ${shoulder[1]} Q${elbow[0]} ${elbow[1]} ${hand[0] + 3 * Z} ${hand[1] + Z}`}
          width={6 * Z}
          className="stroke-brand"
        />
        <ellipse cx={hand[0]} cy={hand[1]} rx={3.5 * Z} ry={2 * Z} strokeWidth="1.4" className="fill-surface stroke-ink" />
        </g>

        {/* Chair back, nearest to us, behind his back */}
        <path d={line([102, 102, 46], [108, 102, 57])} strokeWidth={4 * Z} className="stroke-panel" />
        <Box u={[106, 110]} v={[91, 113]} h={[56, 92]} fill="fill-panel" />
        <path d={line([110, 94, 60], [110, 94, 88])} strokeWidth="1.4" className="stroke-surface/25" />
      </svg>
    </div>
  );
}
